import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { FC, ReactNode } from 'react';
import { Suspense } from 'react';
import type { To } from 'react-router';
import { useNavigation } from 'react-router';
import styled from 'styled-components';

import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';

import { FixtureSelector } from 'components/fixture/FixtureSelector';

import type { BasicFixtureLayout_so5Fixture } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  isolation: isolate;
  padding-bottom: var(--triple-unit);
`;

const FixtureSelectorContainer = styled.div`
  margin: auto;
  position: relative;
  max-width: min(100%, 430px);

  &.big {
    max-width: min(100%, 710px);
  }
`;

type Props = {
  so5Fixture: BasicFixtureLayout_so5Fixture;
  withOnboarding?: boolean;
  navigateTo?: (fixture: string) => To;
  pickerSize?: 'small' | 'big';
  className?: string;
  extraHeader?: ReactNode;
  children?:
    | ReactNode
    | FC<React.PropsWithChildren<{ so5FixtureSlug: string }>>;
  onFixtureChanged?: (fixture: string) => void;
};
export const BasicFixtureLayout = ({
  so5Fixture,
  className,
  navigateTo,
  withOnboarding,
  pickerSize = 'small',
  extraHeader,
  children,
  onFixtureChanged,
}: Props) => {
  const navigation = useNavigation();
  const isNavigating = Boolean(navigation.location);
  const isLaptopAndAbove = useIsLaptop();

  return (
    <Root gap={isLaptopAndAbove ? 3 : 2} className={className}>
      <FullWidthContainer>
        <OnboardingHighlightWrapper
          wrap={withOnboarding}
          highlightName="FixtureSelectorContainer"
        >
          <FixtureSelectorContainer className={pickerSize}>
            <FixtureSelector
              withMask
              navigateTo={navigateTo}
              currentFixture={so5Fixture}
              pickerSize={pickerSize}
              onFixtureChanged={onFixtureChanged}
            />
          </FixtureSelectorContainer>
        </OnboardingHighlightWrapper>
      </FullWidthContainer>
      <Suspense fallback={<LoadingIndicator />}>
        {extraHeader}
        {isNavigating ? (
          <LoadingIndicator />
        ) : (
          <>
            {typeof children === 'function'
              ? children({ so5FixtureSlug: so5Fixture.slug })
              : children}
          </>
        )}
      </Suspense>
    </Root>
  );
};

BasicFixtureLayout.fragments = {
  so5Fixture: gql`
    fragment BasicFixtureLayout_so5Fixture on So5Fixture {
      slug
      ...FixtureSelector_so5Fixture
    }
    ${FixtureSelector.fragments.so5Fixture}
  ` as TypedDocumentNode<BasicFixtureLayout_so5Fixture>,
};
